import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU       from "@/core/misc/decorators/Title_ru_RU";
import TYPES             from "@/core/services/Changelogs/types";

import Core              from "@/core/Core";
import CargoUnitsService from "@/domains/CargoUnits/CargoUnitsService";
import Dimension         from '@/core/types/Dimension';
import Volume            from '@/core/types/Volume';
import Weight            from '@/core/types/Weight';
import Qty               from '@/core/types/Qty';
import PalletType        from '@/domains/CargoUnits/PalletType';
import BaseEntity        from "@/core/misc/BaseEntity";

type SizeType = {
    id: string | number;
    [key: string]: any;
};

@Title_ru_RU_Class("Паллетоместо")
class CargoUnit extends BaseEntity {
    private _selectedSizeType: SizeType = CargoUnitsService.sizeTypeList()[0];
    private _palletType: PalletType;
    private _weightNet: Weight;
    private _weightGross: Weight;

    private _length: Dimension;

    @Title_ru_RU('Ширина одного паллетоместа', TYPES.NUMBER)
    private _width: Dimension;
    @Title_ru_RU('Высота одного паллетоместа', TYPES.NUMBER)
    private _height: Dimension;
    @Title_ru_RU('Объем одного паллетоместа', TYPES.NUMBER)
    private _volume: Volume

    private _qty: Qty;

    private _palletWeight: Weight;

    static isCargoUnit(value: unknown): value is CargoUnit {
        return value instanceof CargoUnit;
    }

    static fromServer(cargoUnitData: any, cargoUnit: CargoUnit = new CargoUnit()): CargoUnit {
        super.fromServer(cargoUnitData, cargoUnit);

        // const palletTypeList = CargoUnitsService.getPalletTypeListForSelect();

        cargoUnit.palletWeight = Core.utils.hydrate(cargoUnitData?.palletType?.weight, Weight.fromServer)!;

        if (Core.utils.isObject(cargoUnitData.palletType)) {
          cargoUnit.palletType = Core.utils.hydrate(cargoUnitData.palletType, PalletType.fromServer)
        } else {
          /** Тип паллета не задан — значит индивидуальные размеры.
           * Подбор стандартного типа паллета по габаритам отключён (закомментирован ниже). */

          /** Присваиваем напрямую в приватные поля чтобы не триггерить calculateVolume(),
           * объём будет присвоен отдельно из cargoUnitData.volume ниже.
           *
           * _palletType остаётся не заданным: selectedSizeType — это режим ввода
           * габаритов, а не тип паллета из справочника */
          cargoUnit._selectedSizeType = CargoUnitsService.sizeTypeList()[1];

          cargoUnit.width  = Core.utils.hydrate(cargoUnitData.width, Dimension.fromServer)!;
          cargoUnit.length = Core.utils.hydrate(cargoUnitData.length, Dimension.fromServer)!;
        }

        if (cargoUnit.palletType && !cargoUnit.palletType.name) {
          cargoUnit.palletType.name = CargoUnitsService.getPalletTypeName(cargoUnit.palletType);
        }
        /** Это присваение нужно, потому что каким-то образом, все числа, что <0.1 превращаются в просто 0
         * таким образом 0.014 объема превращаются в 0, на этапе гидрации объекта с бека, но в data лежит
         * оригинальное значение, и тут мы можем его вот так присвоить */
        cargoUnit.volume = Volume.fromServer(String(cargoUnitData.volume));

        /** Скаляры Serializable разливает по именам сам, но Numeric-типы автомат
         * не подхватывает — их всегда гидрируем принудительно через хелпер. */
        cargoUnit.qty         = Core.utils.hydrate(cargoUnitData.qty, Qty.fromServer)!;

        cargoUnit.weightNet   = Core.utils.hydrate(cargoUnitData.weightNet, Weight.fromServer)!
        cargoUnit.weightGross = Core.utils.hydrate(cargoUnitData.weightGross, Weight.fromServer)!;
        cargoUnit.height      = Core.utils.hydrate(cargoUnitData.height, Dimension.fromServer)!;
        cargoUnit.width       = Core.utils.hydrate(cargoUnitData.width, Dimension.fromServer)!;
        cargoUnit.length      = Core.utils.hydrate(cargoUnitData.length, Dimension.fromServer)!;

        return cargoUnit;
    }

    /**
     * Копия паллетоместа для другого документа (сборка → отгрузка).
     * Паллетоместо принадлежит своему документу (на бэке привязка через document),
     * у нового документа будут свои строки.
     */
    static fromCargoUnit(source: CargoUnit): CargoUnit {
        const result = Core.utils.cloneDeep(source);

        // новая сущность для бэка — сбрасываем id/version, иначе с тем же id
        // бэк воспринял бы её как паллетоместо сборки
        result.resetIdentity();

        return result;
    }

    /** Композитный ключ для группировки одинаковых паллетомест */
    get compositeKey(): string {
      const gross = this.weightGross;
      return `${gross ? gross.toJSON() : ''}-${this.width?.toJSON() ?? ''}-${this.length?.toJSON() ?? ''}-${this.height?.toJSON() ?? ''}-${this.selectedSizeType.id}`;
    }

    // ---- selectedSizeType ----

    get selectedSizeType(): SizeType { return this._selectedSizeType; }

    set selectedSizeType(selectedSizeType: SizeType) {
        this._selectedSizeType = selectedSizeType;

        this.palletType = undefined;
        this.width = undefined;
        this.length = undefined;
        this.volume = undefined;
        this.palletWeight = undefined;
    }

    // ---- palletType ----

    get palletType(): PalletType { return this._palletType; }

    set palletType(palletType: PalletType) {
        if (PalletType.isPalletType(palletType)) {
            this._palletType = palletType;

            this.width = palletType.width;
            this.length = palletType.length;
            this.palletWeight = palletType.weight;
        } else {
            this._palletType = undefined;
        }
    }

    // ---- qty ----

    get qty(): Qty { return this._qty; }
    set qty(qty: Qty) { this._qty = qty; }

    // ---- width ----

    get width(): Dimension { return this._width; }
    set width(width: Dimension) { this._width = width; }

    // ---- height ----

    get height(): Dimension { return this._height; }
    set height(height: Dimension) { this._height = height; }

    // ---- length ----

    get length(): Dimension { return this._length; }
    set length(length: Dimension) { this._length = length; }

    // ---- palletWeight ----
    get palletWeight(): Weight { return this._palletWeight; }
    set palletWeight(weight: Weight) { this._palletWeight = weight; }

    // ---- weightNet ----

    get weightNet(): Weight { return this._weightNet; }
    set weightNet(weightNet: Weight) { this._weightNet = weightNet; }

    // ---- volume (производится от length*width*height) ----

    get volume(): Volume | undefined {
      if (!(this.length instanceof Dimension)) return this._volume;
      if (!(this.width instanceof Dimension)) return this._volume;
      if (!(this.height instanceof Dimension)) return this._volume;

      return this.length.mul(this.width).mul(this.height);
    }

    /** Индивидуальный объём (не рассчитанный, а введённый с бека/пользователем) —
     * актуален только когда нет length/width/height для расчёта. */
    set volume(volume: Volume) { this._volume = volume; }

    // ---- weightGross (производится от weightNet + palletWeight) ----

    get weightGross(): Weight | undefined {
      if (this.weightNet instanceof Weight === false) {
        return undefined;
      }
      // вес паллета хранится в palletType, при индивид. размерах он не указан —
      // прибавлять нечего, брутто равно нетто
      else if (this.selectedSizeType.id === 'individual') {
        return this.weightNet;
      }
      else if (this.palletWeight instanceof Weight === true) {
        return this.weightNet.plus(this.palletWeight);
      }
      // стандартный паллет, но вес паллета ещё не известен (тип не выбран)
      else {
        return undefined;
      }
    }

  set weightGross(weightGross: Weight) {
    this._weightGross = weightGross;
  }

  // ---- totalWeightNet (производится от weightNet*qty) ----

    get totalWeightNet(): Weight | undefined {
      if (!(this.weightNet instanceof Weight)) return undefined;
      if (!(this.qty instanceof Qty)) return undefined;

      return this.weightNet.mul(this.qty);
    }

    // ---- totalWeightGross (производится от weightGross*qty) ----

    get totalWeightGross(): Weight | undefined {
      const gross = this.weightGross;
      if (!gross) return undefined;
      if (!(this.qty instanceof Qty)) return undefined;

      return gross.mul(this.qty);
    }

    // ---- totalVolume (производится от volume*qty) ----

    get totalVolume(): Volume | undefined {
      const vol = this.volume;
      if (!vol) return undefined;
      if (!(this.qty instanceof Qty)) return undefined;

      return vol.mul(this.qty);
    }

    /** Сравнивает два набора сторон паллета без привязки к width/length.
     * Европаллет 1200x800 === 800x1200 */
    private static isSamePalletSize(
      aWidth: Dimension, aLength: Dimension,
      bWidth: Dimension, bLength: Dimension,
    ): boolean {
      if (aWidth.equals(bWidth) && aLength.equals(bLength)) return true;
      if (aWidth.equals(bLength) && aLength.equals(bWidth)) return true;

        return false;
    }
}

export default CargoUnit;
